import { Vehicle } from '../types';
import { PartsCatalogProvider, PartsCatalogProviderId } from '../types/partsCatalog';

/**
 * Normalizes vehicle model names for RockAuto's catalog format
 * Handles common variations and formats them correctly
 */
const normalizeModelForRockAuto = (make: string, model: string): string => {
  let normalized = model.trim();

  // Ford F-Series trucks (F150 → F-150, etc.)
  if (make.toLowerCase() === 'ford') {
    normalized = normalized.replace(/^F(\d{3})$/i, 'F-$1');
    normalized = normalized.replace(/^F\s+(\d{3})$/i, 'F-$1');
  }

  // Chevy/GMC trucks - add space before numbers if missing
  if (['chevrolet', 'gmc'].includes(make.toLowerCase())) {
    normalized = normalized.replace(/^(Silverado|Sierra)(\d{4})$/i, '$1 $2');
  }

  // Ram trucks - just the number
  if (make.toLowerCase() === 'ram') {
    normalized = normalized.replace(/^Ram\s*/i, '');
  }

  // Replace multiple spaces with single space
  normalized = normalized.replace(/\s+/g, ' ');

  return normalized;
};

/**
 * Builds a RockAuto catalog URL for a specific vehicle
 * Uses direct catalog URL with normalized model names
 */
const buildRockAutoUrl = (vehicle: Vehicle): string => {
  // Normalize the model name for RockAuto's format
  const normalizedModel = normalizeModelForRockAuto(vehicle.make, vehicle.model);

  // RockAuto direct catalog URL format: rockauto.com/en/catalog/{make},{year},{model}
  const make = encodeURIComponent(vehicle.make);
  const year = vehicle.year;
  const model = encodeURIComponent(normalizedModel);

  return `https://www.rockauto.com/en/catalog/${make},${year},${model}`;
};

/**
 * Available parts catalog providers
 * Add new catalogs here as they become available
 */
export const PARTS_CATALOG_PROVIDERS: Record<PartsCatalogProviderId, PartsCatalogProvider> = {
  rockauto: {
    id: 'rockauto',
    name: 'RockAuto',
    icon: 'car',
    buildUrl: buildRockAutoUrl,
    enabled: true,
  },
  // Future catalogs can be added here
  autozone: {
    id: 'autozone',
    name: 'AutoZone',
    icon: 'shop',
    buildUrl: (vehicle: Vehicle) => {
      // Placeholder - implement when adding AutoZone support
      return '#';
    },
    enabled: false,
  },
  oreilly: {
    id: 'oreilly',
    name: "O'Reilly Auto Parts",
    icon: 'tool',
    buildUrl: (vehicle: Vehicle) => {
      // Placeholder - implement when adding O'Reilly support
      return '#';
    },
    enabled: false,
  },
  advanceauto: {
    id: 'advanceauto',
    name: 'Advance Auto Parts',
    icon: 'setting',
    buildUrl: (vehicle: Vehicle) => {
      // Placeholder - implement when adding Advance Auto support
      return '#';
    },
    enabled: false,
  },
  napa: {
    id: 'napa',
    name: 'NAPA Auto Parts',
    icon: 'build',
    buildUrl: (vehicle: Vehicle) => {
      // Placeholder - implement when adding NAPA support
      return '#';
    },
    enabled: false,
  },
};

/**
 * Gets list of enabled parts catalog providers
 */
export const getEnabledCatalogs = (): PartsCatalogProvider[] => {
  return Object.values(PARTS_CATALOG_PROVIDERS).filter(provider => provider.enabled);
};

/**
 * Opens a parts catalog in a new tab for the specified vehicle
 * @param catalogId - The ID of the catalog to open
 * @param vehicle - The vehicle to search parts for
 * @returns true if successfully opened, false if vehicle or catalog invalid
 */
export const openPartsCatalog = (
  catalogId: PartsCatalogProviderId,
  vehicle?: Vehicle
): boolean => {
  if (!vehicle) {
    console.warn('Cannot open parts catalog: No vehicle specified');
    return false;
  }

  const catalog = PARTS_CATALOG_PROVIDERS[catalogId];

  if (!catalog) {
    console.warn(`Cannot open parts catalog: Unknown catalog ID "${catalogId}"`);
    return false;
  }

  if (!catalog.enabled) {
    console.warn(`Cannot open parts catalog: Catalog "${catalog.name}" is not enabled`);
    return false;
  }

  try {
    const url = catalog.buildUrl(vehicle);

    // Open in new tab with security flags
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    console.error('Error opening parts catalog:', error);
    return false;
  }
};

/**
 * Validates if a vehicle has the minimum required information for catalog lookup
 */
export const isVehicleValidForCatalog = (vehicle?: Vehicle): boolean => {
  if (!vehicle) {
    return false;
  }

  // Must have year, make, and model at minimum
  return !!(vehicle.year && vehicle.make && vehicle.model);
};
